/**
 * Weather alert layer renderer.
 * ScatterplotLayer with severity-coded colors for NWS severe weather warnings.
 * Uses weather-specific icons for different event types (tornado, storm, flood, etc.).
 */

import { ScatterplotLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent, Severity } from '@/sources/base-source';

/** Layer IDs handled by this renderer. */
export const WEATHER_LAYER_IDS = new Set(['weather-alerts']);

/** Weather severity colors — matching WorldMonitor. */
const WEATHER_SEVERITY_COLOR: Record<Severity, [number, number, number]> = {
  critical: [255, 0, 0],       // red — Extreme
  high: [255, 100, 0],         // orange — Severe
  medium: [255, 170, 0],       // amber — Moderate
  low: [100, 150, 255],        // blue — advisory
  info: [100, 150, 255],       // blue — default
};

/** Weather event radius — uniform 8px matching WorldMonitor. */
function getWeatherRadius(_event: NormalizedEvent): number {
  return 25000;
}

/** Create deck.gl layers for weather alert data. */
export function createWeatherLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  const opacity = Math.min(1, 0.3 + zoom * 0.1);
  const layers: Layer[] = [];

  // Outer glow for critical/high weather events
  const severeEvents = events.filter(
    (e) => e.severity === 'critical' || e.severity === 'high',
  );

  if (severeEvents.length > 0) {
    layers.push(new ScatterplotLayer<NormalizedEvent>({
      id: 'weather-glow',
      data: severeEvents,
      pickable: false,
      filled: true,
      radiusUnits: 'meters' as const,
      radiusMinPixels: 12,
      radiusMaxPixels: 30,
      getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
      getFillColor: (d) => {
        const color = WEATHER_SEVERITY_COLOR[d.severity ?? 'info'];
        return [...color, 50] as [number, number, number, number];
      },
      getRadius: (d) => getWeatherRadius(d) + 8000,
      opacity: opacity * 0.4,
    }));
  }

  // Core weather dots
  layers.push(new ScatterplotLayer<NormalizedEvent>({
    id: 'weather-dots',
    data: events,
    pickable: true,
    filled: true,
    radiusUnits: 'meters' as const,
    radiusMinPixels: 8,
    radiusMaxPixels: 20,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getFillColor: (d) => WEATHER_SEVERITY_COLOR[d.severity ?? 'info'],
    getRadius: (d) => getWeatherRadius(d),
    opacity,
    updateTriggers: {
      getFillColor: [events.length],
      getRadius: [events.length],
    },
  }));

  return layers;
}
